import {
  dmsAttachmentSchema,
  dmsListResponseSchema,
  type DmsAttachment,
  type DmsAttachmentUpdateInput,
  type DmsCategory,
  type DmsEntityType,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiDownload, apiFetch, apiSend, saveBlob, type ApiError } from '@/lib/api-client';

export interface DmsListParams {
  entityType: DmsEntityType;
  entityId?: string | undefined;
  category?: DmsCategory | undefined;
  search?: string | undefined;
}

export interface DmsUploadInput {
  entityType: DmsEntityType;
  entityId?: string | undefined;
  category: DmsCategory;
  title?: string | undefined;
  description?: string | undefined;
  tags: readonly string[];
  file: File;
}

function listQuery(params: DmsListParams): string {
  const query = new URLSearchParams({
    entityType: params.entityType,
    page: '1',
    pageSize: '200',
    sort: 'createdAt:desc',
  });
  if (params.entityId !== undefined) {
    query.set('entityId', params.entityId);
  }
  if (params.category !== undefined) {
    query.set('category', params.category);
  }
  if (params.search !== undefined && params.search.trim().length > 0) {
    query.set('search', params.search.trim());
  }
  return query.toString();
}

export const dmsKeys = {
  all: ['dms'] as const,
  list: (params: DmsListParams) => ['dms', 'list', params] as const,
  preview: (id: string) => ['dms', 'preview', id] as const,
};

export function useDmsAttachments(
  params: DmsListParams,
  enabled = true,
): UseQueryResult<ReturnType<typeof dmsListResponseSchema.parse>, ApiError> {
  return useQuery({
    queryKey: dmsKeys.list(params),
    queryFn: () => apiFetch(`/api/v1/dms?${listQuery(params)}`, dmsListResponseSchema),
    enabled,
  });
}

export function useUploadDmsAttachment(): UseMutationResult<
  DmsAttachment,
  ApiError,
  DmsUploadInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => {
      const formData = new FormData();
      formData.append('entityType', input.entityType);
      if (input.entityId !== undefined) {
        formData.append('entityId', input.entityId);
      }
      formData.append('category', input.category);
      if (input.title !== undefined) {
        formData.append('title', input.title);
      }
      if (input.description !== undefined) {
        formData.append('description', input.description);
      }
      formData.append('tags', JSON.stringify(input.tags));
      formData.append('file', input.file, input.file.name);
      return apiFetch('/api/v1/dms/upload', dmsAttachmentSchema, {
        method: 'POST',
        formData,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dmsKeys.all });
    },
  });
}

export function useUpdateDmsAttachment(): UseMutationResult<
  DmsAttachment,
  ApiError,
  { id: string; input: DmsAttachmentUpdateInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiFetch(`/api/v1/dms/${id}`, dmsAttachmentSchema, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dmsKeys.all });
    },
  });
}

export function useDeleteDmsAttachment(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiSend(`/api/v1/dms/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dmsKeys.all });
    },
  });
}

export function useDmsPreview(
  attachment: DmsAttachment | null,
  enabled: boolean,
): UseQueryResult<Blob, ApiError> {
  return useQuery({
    queryKey: dmsKeys.preview(attachment?.id ?? ''),
    enabled: attachment !== null && enabled,
    queryFn: async () => {
      const result = await apiDownload(
        `/api/v1/dms/${attachment?.id ?? ''}/preview`,
        attachment?.originalName ?? 'download',
      );
      return result.blob;
    },
    staleTime: 60_000,
  });
}

export async function downloadDmsAttachment(attachment: DmsAttachment): Promise<void> {
  const result = await apiDownload(
    `/api/v1/dms/${attachment.id}/download`,
    attachment.originalName,
  );
  saveBlob(result);
}
